import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { CoachOfferAccompaniedCard } from "@/modules/coachOffers/components/coachOfferAccompaniedCard"
import { useCoachOffers } from "@/modules/coachOffers/hooks/useCoachOffers"
import { useCoachOfferSidebar } from "@/modules/coachOffers/hooks/useCoachOfferSidebar"
import {
  ACCOMPANIED_OFFER_TYPES,
  ALL_OFFER_FILTER,
  type OfferTypeFilter,
} from "@/modules/coachOffers/page/coachOffersPage"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { useQueryParam } from "@/modules/utils/navigation/useQueryParam"
import { PlusIcon } from "lucide-react"
import { useMemo } from "react"

type CoachOfferAccompaniedListProps = { selectedFilter: OfferTypeFilter } & React.ComponentProps<"div">

export function CoachOfferAccompaniedList({ selectedFilter, ...props }: CoachOfferAccompaniedListProps) {
  const { navigateToCoachOfferCreation, navigateToCoachOfferResume } = useCoachOfferSidebar()
  const { data: offers } = useCoachOffers()
  const [search] = useQueryParam("search")

  const filteredOffers = useMemo(() => {
    const typeFiltered =
      selectedFilter === ALL_OFFER_FILTER
        ? offers.filter((o) => ACCOMPANIED_OFFER_TYPES.includes(o.type))
        : offers.filter((o) => o.type === selectedFilter)
    if (!search) return typeFiltered

    const lowerSearch = search.toLowerCase()
    return typeFiltered.filter(
      (o) => o.name.toLowerCase().includes(lowerSearch) || o.description?.toLowerCase().includes(lowerSearch),
    )
  }, [offers, selectedFilter, search])

  return (
    <div className={cn("flex flex-wrap gap-3", props.className)} {...props}>
      <div className="flex min-h-40 w-full flex-col justify-between gap-4 rounded-xl border border-dashed border-primary-300 p-4 lg:w-[49%]">
        <Text fontStyle="TM" className="text-textOnSurface-default">
          {word("coach.offers.add.title")}
        </Text>
        <Button
          onClick={() => {
            void navigateToCoachOfferCreation()
          }}
          variant="primary"
          className="w-fit"
          size="lg"
        >
          <PlusIcon size={16} />
          {word("global.new")}
        </Button>
      </div>

      {filteredOffers.map((offer) => (
        <CoachOfferAccompaniedCard
          key={offer.id}
          offer={offer}
          className="min-h-40 lg:w-[49%]"
          onClick={() => void navigateToCoachOfferResume(offer.id)}
        />
      ))}
    </div>
  )
}
